import { msg, plural } from '@lingui/core/macro';
import type { AxiosHeaders, AxiosRequestConfig } from 'axios';

import { Logger } from '@rover/rsdk/src/modules/Logging';

import {
  ApiErrorMessage,
  DEFAULT_ERROR_MESSAGE,
  diagnosticErrorString,
  toDiagnosticApiErrorMessage,
} from './apiErrorMessage';
import { emitApiErrorMetric } from './emitMetricUtils';

export const DEFAULT_CF_ERROR_MESSAGE = ApiErrorMessage.fromDescriptor(
  msg({
    id: 'apiError.generic.cloudflareChallenge',
    message: `We were unable to process your request. Please try again shortly or contact Customer Support. (Error code: #15172)`,
  })
);

// The wait is only ever reported inside the server's prose ("Expected available in 3098
// seconds."), so the digits are all there is to go on. Waits are rounded up, so anything
// under a minute reports as one minute rather than as an expired countdown.
const createThrottleError = (rawMessage: string): ApiErrorMessage => {
  const digitsMatcher = rawMessage.match(/\d+/);
  const matchedDigits: string = digitsMatcher ? digitsMatcher[0] : '';
  const seconds = parseInt(matchedDigits, 10);

  if (Number.isNaN(seconds)) {
    return ApiErrorMessage.fromDescriptor(
      msg({
        id: 'apiError.generic.throttleUnknownWait',
        message: `Please slow down and try again in a few minutes.`,
      })
    );
  }

  const minutes = Math.ceil(seconds / 60);

  if (minutes < 60) {
    return ApiErrorMessage.fromDescriptor(
      msg({
        id: 'apiError.generic.throttleMinutes',
        message: plural(minutes, {
          one: 'You have performed this action too many times. Please try again in # minute.',
          other: 'You have performed this action too many times. Please try again in # minutes.',
        }),
      })
    );
  }

  const hours = Math.ceil(minutes / 60);

  if (hours < 24) {
    return ApiErrorMessage.fromDescriptor(
      msg({
        id: 'apiError.generic.throttleHours',
        message: plural(hours, {
          one: 'You have performed this action too many times. Please try again in # hour.',
          other: 'You have performed this action too many times. Please try again in # hours.',
        }),
      })
    );
  }

  return ApiErrorMessage.fromDescriptor(
    msg({
      id: 'apiError.generic.throttleTomorrow',
      message: `You have performed this action too many times. Please try again tomorrow.`,
    })
  );
};

/**
 * The prose out of a form's `errors`, which `account_views` returns whole under `error`
 * (`{"email": ["Enter a valid email address."]}`). A non-prose value holds internals.
 */
const fieldErrorProse = (errorData: Record<string, unknown>): string[] => {
  const values = Object.values(errorData);

  if (!values.length) return [];

  const prose: string[] = [];

  for (const value of values) {
    if (typeof value === 'string') {
      prose.push(value);
    } else if (Array.isArray(value) && value.length && value.every((v) => typeof v === 'string')) {
      // One per field: a consumer renders the last message, so earlier ones are dropped.
      prose.push(value.join(' '));
    } else {
      return [];
    }
  }

  return prose;
};

const parseObjectErrors = (json: Record<string, unknown> = {}): ApiErrorMessage[] => {
  const errors: ApiErrorMessage[] = [];

  let { error_description: errorDescription, non_field_errors: nonFieldErrors, error } = json;
  const { detail, error_type: errorType } = json;

  if (detail) {
    errors.push(ApiErrorMessage.fromApiResponse(detail));
  }

  if (error && typeof error === 'object' && errorType !== 'field_validation') {
    const errorData = error as Record<string, unknown>;
    if (errorData.error_description) {
      errorDescription = errorData.error_description;
    } else if (errorData.error_status) {
      errorDescription = errorData.error_status;
    }

    if (errorData.non_field_errors) nonFieldErrors = errorData.non_field_errors;
    if (!errorDescription && !nonFieldErrors) {
      const fieldProse = fieldErrorProse(errorData);

      errors.push(
        ...(fieldProse.length
          ? fieldProse.map(ApiErrorMessage.fromApiResponse)
          : // Holds internals, not prose: `fromApiResponse` classifies the object as diagnostic.
            [ApiErrorMessage.fromApiResponse(error)])
      );
      error = undefined;
    }
  }

  if (errorDescription) errors.push(ApiErrorMessage.fromApiResponse(errorDescription));
  if (typeof error === 'string') errors.push(ApiErrorMessage.fromApiResponse(error));

  if (Array.isArray(nonFieldErrors)) {
    errors.push(...nonFieldErrors.map(ApiErrorMessage.fromApiResponse));
  } else if (nonFieldErrors) {
    errors.push(ApiErrorMessage.fromApiResponse(nonFieldErrors));
  }

  if (!errors.length && typeof json === 'object') {
    Object.keys(json).forEach((errorKey: string) => {
      let fieldErrors: ApiErrorMessage[] = [];
      if (Array.isArray(json[errorKey])) {
        const jsonArray = json[errorKey] as unknown[];
        const unnested: unknown[] = [];
        jsonArray.forEach((err) => {
          const nested = parseObjectErrors(err as Record<string, unknown>);
          // A list of prose is the DRF field-error shape; only a nested serializer nests
          // any further.
          if (nested.length) fieldErrors.push(...nested);
          else unnested.push(err);
        });
        // Joined, not pushed one by one: a consumer renders the last entry only. Classified
        // before the join, since joining hands `fromApiResponse` a string and an entry that
        // is not prose then renders as `[object Object]` instead of as a diagnostic.
        if (unnested.length) {
          const isProse = unnested.every((v) => typeof v === 'string' || typeof v === 'number');
          fieldErrors.push(
            isProse
              ? ApiErrorMessage.fromApiResponse(unnested.join(' '))
              : diagnosticErrorString(JSON.stringify(unnested))
          );
        }
      } else {
        fieldErrors = parseObjectErrors(json[errorKey] as Record<string, unknown>);
      }

      if (fieldErrors.length) {
        errors.push(...fieldErrors);
      } else {
        // Prose is the common case; an object reaching here would render as `[object Object]`.
        errors.push(ApiErrorMessage.fromApiResponse(json[errorKey]));
      }
    });
  }

  return errors;
};

export type Response = {
  status?: number;
  data?: Record<string, unknown> | string;
  config?: AxiosRequestConfig;
  response?: Record<string, unknown>;
  responseText?: string;
  headers?: AxiosHeaders | Headers | Record<string, string>;
};

const parseApiErrors = (response: Response, useAPIErrorFor: number[] = []): ApiErrorMessage[] => {
  const defaultGenericErrorCodes = [403, 405, 422, 500];
  const codesWithGenericError = defaultGenericErrorCodes.filter(
    (code) => !useAPIErrorFor.includes(code)
  );
  const errors: ApiErrorMessage[] = [];
  let json;
  const status = response.status ? response.status : 0;
  const headers = response.headers || response?.config?.headers;
  const isHTML = (headers?.['Content-Type'] || headers?.['content-type'] || '').includes(
    'text/html'
  );
  emitApiErrorMetric(response);
  const errorType =
    response.data && typeof response.data === 'object' ? response.data.error_type : '';
  if (
    errorType === 'phone_verification_send_throttled' ||
    errorType === 'phone_verification_verify_throttled'
  ) {
    const throttleDetail =
      response.data && typeof response.data === 'object'
        ? response.data.error_description || response.data.detail
        : '';
    errors.push(ApiErrorMessage.fromApiResponse(throttleDetail));
  } else if (status === 429) {
    // Throttles raised by DRF itself keep the default `detail` body, while views routed
    // through api_exception_handler are reserialized to `error_description`. Both shapes
    // reach here, and only one of them includes the wait.
    const throttleDetail =
      response.data && typeof response.data === 'object'
        ? response.data.error_description || response.data.detail
        : '';
    errors.push(createThrottleError(String(throttleDetail)));
  } else if (status === 403 && headers?.['cf-mitigated'] === 'challenge') {
    // CF challenge flow
    errors.push(DEFAULT_CF_ERROR_MESSAGE);
    Logger.warn(toDiagnosticApiErrorMessage(DEFAULT_CF_ERROR_MESSAGE), {
      path: response?.config?.url || 'unkown',
      headers: JSON.stringify(headers),
      env: process.env.JS_ENV_SERVER ? 'Server-Side' : 'Client-Side',
    });
  } else if (status === 403 && isHTML) {
    // CF block flow
    errors.push(
      diagnosticErrorString(`Non-JSON response, with these headers: ${Object.keys(headers || {})}`)
    );
    Logger.warn('Non-JSON response error', {
      path: response?.config?.url || 'unkown',
      headers: JSON.stringify(headers),
      env: process.env.JS_ENV_SERVER ? 'Server-Side' : 'Client-Side',
    });
  } else if (codesWithGenericError.includes(status)) {
    errors.push(DEFAULT_ERROR_MESSAGE);
  } else {
    json =
      response.response ||
      (response.responseText && JSON.parse(response.responseText)) ||
      response.data;
  }

  if (json) {
    errors.push(...parseObjectErrors(json));
  }

  return errors;
};

export default parseApiErrors;
